import { migrateTraversal } from "../helpers/migrateTraversal";
import { IWebsiteBuilderMigrationNode, IWebsiteBuilderSchemaVersionMigrationNode } from "../helpers/migrationNode";
import { WebsiteBuilderMigrationError } from "../helpers/migrationError";
import { clamp } from "lodash";

type TMigrateFontSizeV5Result = {
  desktop: number;
  tablet: number;
  mobile: number;
}

interface IMigrateElementV4Input extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: string;
    settings: Record<string, unknown>;
  }
}

export interface IMigrateHeadingV4Input extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "heading";
    settings: {
      fontSize: number;
      lineHeight: number;
    }
  }
}

export interface IMigrateParagraphV4Input extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "paragraph";
    settings: {
      fontSize: number;
      lineHeight: number;
    }
  }
}

export interface IMigrateHeadingV5Result extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "heading";
    settings: {
      fontSize: TMigrateFontSizeV5Result;
      lineHeight: number;
    }
  }
}

export interface IMigrateParagraphV5Result extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "paragraph";
    settings: {
      fontSize: TMigrateFontSizeV5Result;
      lineHeight: number;
    }
  }
}

function isMigrateElementV4Input(node: any): node is IMigrateElementV4Input {
  const partialNode: Partial<IMigrateElementV4Input> = node;

  return (
    partialNode?.type === "element" &&
    partialNode.element?.type != null &&
    "settings" in partialNode.element
  );
}

function isOptionalNumericV4InputValid(value: unknown) {
  return value == null || (typeof value === "number" && !isNaN(value));
}

function isMigrateHeadingV4Input(node: IMigrateElementV4Input): node is IMigrateHeadingV4Input {
  return (
    node.element.type === "heading" &&
    node.element.settings != null &&
    "fontSize" in node.element.settings &&
    "lineHeight" in node.element.settings &&
    isOptionalNumericV4InputValid(node.element.settings.fontSize) &&
    isOptionalNumericV4InputValid(node.element.settings.lineHeight)
  );
}

function isMigrateParagraphV4Input(node: IMigrateElementV4Input): node is IMigrateParagraphV4Input {
  return (
    node.element.type === "paragraph" &&
    node.element.settings != null &&
    "fontSize" in node.element.settings &&
    "lineHeight" in node.element.settings &&
    isOptionalNumericV4InputValid(node.element.settings.fontSize) &&
    isOptionalNumericV4InputValid(node.element.settings.lineHeight)
  );
}

export function migrateSchemaV4ToV5(node: IWebsiteBuilderSchemaVersionMigrationNode): IWebsiteBuilderSchemaVersionMigrationNode {
  if (node.schemaVersion !== 4) {
    throw new WebsiteBuilderMigrationError({
      type: "cannot-migrate-from-schema-version",
      schemaVersion: node.schemaVersion,
      nextSchemaVersion: 5,
    });
  }

  function migrateNode(prev: IWebsiteBuilderMigrationNode): IWebsiteBuilderMigrationNode {
    const throwElementMissingFieldError = () => {
      throw new WebsiteBuilderMigrationError({
        type: "element-missing-field",
        schemaVersion: 4,
        nextSchemaVersion: 5,
      });
    };
    
    if (prev?.type === "element") {
      if (!isMigrateElementV4Input(prev)) {
        throwElementMissingFieldError();
      } else {
        if (prev.element.type === "heading" || prev.element.type === "paragraph") {
          if (!isMigrateHeadingV4Input(prev) && !isMigrateParagraphV4Input(prev)) {
            throwElementMissingFieldError();
          } else {
            const prevFontSize = prev.element.settings.fontSize;
            const prevLineHeight = prev.element.settings.lineHeight;

            const fontSizeV5Result: TMigrateFontSizeV5Result = {
              desktop: prevFontSize,
              tablet: prevFontSize,
              mobile: prevFontSize,
            };

            let lineHeightV5Result: number = null;

            if (prevLineHeight != null && prevFontSize != null && prevFontSize > 0) {
              lineHeightV5Result = clamp(Math.round((prevLineHeight / prevFontSize) * 100) / 100, 1, 2);
            }

            if (prev.element.type === "heading") {
              const headingV5Result: IMigrateHeadingV5Result = {
                ...prev,
                element: {
                  ...prev.element,
                  settings: {
                    ...prev.element.settings,
                    fontSize: fontSizeV5Result,
                    lineHeight: lineHeightV5Result,
                  },
                },
              };

              return headingV5Result;
            } else {
              const paragraphV5Result: IMigrateParagraphV5Result = {
                ...prev,
                element: {
                  ...prev.element,
                  settings: {
                    ...prev.element.settings,
                    fontSize: fontSizeV5Result,
                    lineHeight: lineHeightV5Result,
                  },
                },
              };

              return paragraphV5Result;
            }            
          }
        } else {
          return prev;
        }
      }
    } else {
      return prev;
    }
  }

  return {
    ...migrateTraversal(node, migrateNode),
    schemaVersion: 5,
  };
}